import { useRef } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { track } from "@/lib/track";
import { useMembership } from "@/hooks/useMembership";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

import breathing from "@/assets/journey/breathing.webp.asset.json";
import grounding from "@/assets/journey/grounding.webp.asset.json";
import somatic from "@/assets/journey/somatic.webp.asset.json";
import eftTapping from "@/assets/journey/eft-tapping.webp.asset.json";
import community from "@/assets/journey/community.webp.asset.json";
import journal from "@/assets/journey/journal.webp.asset.json";
import therapyApproaches from "@/assets/journey/therapy-approaches.webp.asset.json";
import sounds from "@/assets/journey/sounds.webp.asset.json";

/**
 * Homepage platform overview — a purely visual carousel.
 *
 * The illustrations already carry their own titles and short descriptions,
 * so no extra card copy is shown. Non-members see the same artwork with a
 * soft lock and are sent to the membership page; members and admins go
 * straight to the area.
 */

type Slide = {
  label: string;
  src: string;
  to: "/learn" | "/library" | "/community" | "/somatic" | "/journal" | "/sounds" | "/therapists";
  memberOnly: boolean;
};

const SLIDES: Slide[] = [
  { label: "Breathing", src: breathing.url, to: "/library", memberOnly: true },
  { label: "Grounding", src: grounding.url, to: "/library", memberOnly: true },
  { label: "Somatic Recovery", src: somatic.url, to: "/somatic", memberOnly: true },
  { label: "EFT & Tapping", src: eftTapping.url, to: "/library", memberOnly: true },
  { label: "Community", src: community.url, to: "/community", memberOnly: true },
  { label: "Journal & Dreams", src: journal.url, to: "/journal", memberOnly: true },
  { label: "Sounds & Meditations", src: sounds.url, to: "/sounds", memberOnly: true },
  { label: "Therapy Approaches", src: therapyApproaches.url, to: "/learn", memberOnly: false },
];

export function MembershipFeaturesSection() {
  const { isMember, isAdmin } = useMembership();
  const hasAccess = isMember || isAdmin;

  // Touch/trackpad swipes should never fire the card's navigation. We record
  // where the pointer went down and swallow the click if it moved like a drag.
  const dragOrigin = useRef<{ x: number; y: number } | null>(null);
  const wasDragged = useRef(false);

  const onPointerDown = (e: React.PointerEvent) => {
    dragOrigin.current = { x: e.clientX, y: e.clientY };
    wasDragged.current = false;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const start = dragOrigin.current;
    if (!start) return;
    if (Math.abs(e.clientX - start.x) > 8 || Math.abs(e.clientY - start.y) > 8) {
      wasDragged.current = true;
    }
  };

  return (
    <section aria-labelledby="membership-features-heading">
      <div className="mx-auto max-w-2xl text-center">
        <h2
          id="membership-features-heading"
          className="font-display text-xl leading-tight tracking-tight text-foreground sm:text-3xl"
        >
          Support for Every Stage of Your Journey
        </h2>
      </div>

      <Carousel
        opts={{
          align: "start",
          loop: true,
          dragFree: false,
          skipSnaps: false,
          watchDrag: true,
          dragThreshold: 6,
          duration: 26,
        }}
        className="mt-5 sm:mt-6"
        aria-label="Recovery Trauma™ features"
      >
        <CarouselContent className="-ms-4 cursor-grab active:cursor-grabbing">
          {SLIDES.map((s) => {
            const locked = s.memberOnly && !hasAccess;
            return (
              <CarouselItem
                key={s.label}
                className="basis-[85%] ps-4 sm:basis-1/2 lg:basis-1/3"
              >
                <Link
                  to={locked ? "/membership" : s.to}
                  draggable={false}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onDragStart={(e) => e.preventDefault()}
                  onClick={(e) => {
                    if (wasDragged.current) {
                      e.preventDefault();
                      e.stopPropagation();
                      return;
                    }
                    track(
                      "cta_click",
                      { cta: locked ? "become_member" : "feature_open" },
                      { source: `home_feature_${s.label}` },
                    );
                  }}
                  aria-label={locked ? `${s.label} — access with membership` : s.label}
                  className="group relative block touch-pan-y select-none overflow-hidden rounded-[22px] border border-gold/30 bg-card shadow-[0_10px_30px_-24px_rgba(120,90,20,0.55)] transition hover:-translate-y-1 hover:border-gold/60"
                >
                  <img
                    src={s.src}
                    alt={s.label}
                    loading="lazy"
                    draggable={false}
                    className="pointer-events-none block h-auto w-full select-none"
                  />
                </Link>
              </CarouselItem>
            );
          })}
        </CarouselContent>




        <div className="mt-2 flex items-center justify-center gap-3">
          <CarouselPrevious className="static translate-y-0 border-gold/40 bg-card/80 text-[var(--gold-deep)] hover:bg-gold-light/40" />
          <CarouselNext className="static translate-y-0 border-gold/40 bg-card/80 text-[var(--gold-deep)] hover:bg-gold-light/40" />
        </div>
      </Carousel>

      {!hasAccess && (
        <div className="mx-auto mt-5 max-w-xl text-center">
          <Link
            to="/membership"
            onClick={() =>
              track("cta_click", { cta: "become_member" }, { source: "home_membership_card" })
            }
            className="btn-gold btn-gold--compact-mobile group"
          >
            Access with Membership
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      <p className="mx-auto mt-3 max-w-xl text-center text-xs text-muted-foreground">
        <Link
          to="/membership-benefits"
          onClick={() =>
            track("cta_click", { cta: "membership_benefits" }, { source: "home_features" })
          }
          className="font-medium text-[var(--gold-deep)] hover:underline"
        >
          What's included in your Recovery Trauma™ Membership
        </Link>
      </p>
    </section>
  );
}
